import type { MetaDescriptor } from "react-router";

import heroEnDesktop from "../../assets/landing/hero-en-desktop.jpg";
import heroZhDesktop from "../../assets/landing/hero-zh-desktop.jpg";
import {
  LANDING_PATHS,
  type LandingLang,
  type LandingVariant,
  SUPPORTED_LANGS,
} from "./constants";

const BASE_URL =
  import.meta.env.VITE_SITE_BASE_URL ?? "https://e-paper.sph.com.sg";

interface LandingMetaCopy {
  title: string;
  description: string;
  locale: string;
  ogImage: string;
}

const COPY: Record<LandingLang, LandingMetaCopy> = {
  en: {
    title: "SPH E-paper",
    description:
      "Subscribe to SPH E-paper for full access to The Straits Times, Lianhe Zaobao, and more.",
    locale: "en_SG",
    ogImage: heroEnDesktop,
  },
  zh: {
    title: "新报业媒体电子报",
    description: "订阅新报业媒体电子报，畅读海峡时报、联合早报等多种报刊。",
    locale: "zh_SG",
    ogImage: heroZhDesktop,
  },
};

/** Route meta for the six landing pages: each A/B variant canonicalises to its control, never noindex. */
export function landingMeta(
  lang: LandingLang,
  variant: LandingVariant,
): MetaDescriptor[] {
  const { title, description, locale, ogImage } = COPY[lang];
  const canonical = `${BASE_URL}${LANDING_PATHS.control[lang]}`;

  const meta: MetaDescriptor[] = [
    { title },
    { name: "description", content: description },
    { name: "robots", content: "index, follow" },
    { tagName: "link", rel: "canonical", href: canonical },
    { property: "og:type", content: "website" },
    { property: "og:url", content: canonical },
    { property: "og:title", content: title },
    { property: "og:description", content: description },
    { property: "og:image", content: `${BASE_URL}${ogImage}` },
    { property: "og:locale", content: locale },
  ];

  // The language alternates belong to the canonical pair only.
  if (variant === "control") {
    for (const alternate of SUPPORTED_LANGS) {
      meta.push({
        tagName: "link",
        rel: "alternate",
        hrefLang: alternate,
        href: `${BASE_URL}${LANDING_PATHS.control[alternate]}`,
      });
    }
  }

  return meta;
}
